/**
 * FR-017 #5 — a document's extracted rows, as a line link sees them.
 *
 * A `line` anchor is the row's index (`anchor_ref`) into the rows under the
 * field the extraction recorded (`line_items_field`, else `line_items`). Two
 * rules here are mirrors of `libs/twin_links/hofsync_twin_links/lines.py`,
 * because the server applies them to the same rows and a disagreement would be
 * a picker offering what the server refuses:
 *
 * - `documentRows` keeps a non-object entry in place as `{}` — dropping it
 *   would shift every later row onto its neighbour's index.
 * - `isAnchorableRow` is `line_fingerprint`'s "has an identity" rule: a
 *   non-blank name/description, or any parseable number among
 *   quantity/unit_price/amount. A row without one is refused
 *   `anchor_line_unidentifiable`.
 */

import i18n from '../../i18n';

export type LineRow = Record<string, unknown>;

/** The metadata slice the rows are read from (`GET /user-documents/{id}/metadata`). */
export interface LineRowsSource {
  line_items_field?: string | null;
  extracted_facts?: Record<string, { value?: unknown } | undefined> | null;
}

const DEFAULT_LINE_FIELD = 'line_items';

const isRowArray = (value: unknown): value is unknown[] =>
  Array.isArray(value) && value.length > 0 && value.every((row) => row && typeof row === 'object' && !Array.isArray(row));

/**
 * The field the rows live under: the one extraction recorded; else `line_items`;
 * else — for a sidecar written before the field was recorded, whose rows sit
 * under another name (payment advice: `referenced_documents`) — the ONE fact
 * holding an array of row objects. Two such facts is a guess, so none.
 * Mirrors `hofsync_twin_links.row_field`.
 */
export function rowField(metadata: LineRowsSource | null | undefined): string | null {
  const facts = metadata?.extracted_facts;
  if (!facts || typeof facts !== 'object') return null;
  if (metadata?.line_items_field) return metadata.line_items_field;
  if (facts[DEFAULT_LINE_FIELD] !== undefined) return DEFAULT_LINE_FIELD;
  const candidates = Object.keys(facts).filter((key) => isRowArray(facts[key]?.value));
  return candidates.length === 1 ? candidates[0] : null;
}

export function documentRows(metadata: LineRowsSource | null | undefined): LineRow[] {
  const field = rowField(metadata);
  if (!field) return [];
  const value = metadata?.extracted_facts?.[field]?.value;
  if (!Array.isArray(value)) return [];
  return value.map((row) => (row && typeof row === 'object' && !Array.isArray(row) ? (row as LineRow) : {}));
}

/**
 * A row's number as a canonical decimal string (`"22,50"` → `"22.5"`), or
 * `null`. The LAST of `,` and `.` is the decimal separator when both appear; a
 * lone `,` is one. Same convention as the lib's `parse_line_number`.
 */
export function parseLineNumber(raw: unknown): string | null {
  if (raw === null || raw === undefined || typeof raw === 'boolean') return null;
  let text: string;
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return null;
    text = String(raw);
  } else {
    text = String(raw).replace(/[^0-9,.-]/g, '');
    if (!/\d/.test(text)) return null;
    if (text.includes(',') && text.includes('.')) {
      text =
        text.lastIndexOf(',') > text.lastIndexOf('.') ? text.replace(/\./g, '').replace(',', '.') : text.replace(/,/g, '');
    } else if (text.includes(',')) {
      text = text.replace(',', '.');
    }
  }
  if (!/^-?(\d+\.?\d*|\.\d+)$/.test(text)) return null;
  const n = Number(text);
  if (!Number.isFinite(n)) return null;
  // Strip trailing zeros the way Decimal.normalize does, without exponent form.
  let [int, frac = ''] = text.replace(/^(-?)0+(?=\d)/, '$1').split('.');
  frac = frac.replace(/0+$/, '');
  if (int === '' || int === '-') int = `${int}0`;
  const out = frac ? `${int}.${frac}` : int;
  return out === '-0' ? '0' : out;
}

/**
 * What a person TYPES, read as German: `,` is the decimal comma, `.` only groups
 * thousands (`1.234` is 1234 — read as 1.234 it would silently change the
 * value; codex r2 over #5). A dot that does not group exactly three digits is
 * refused rather than guessed. Canonical decimal string, or `null`.
 */
export function parseGermanInput(raw: string): string | null {
  const text = raw.trim();
  if (!/^-?\d{1,3}(\.\d{3})*(,\d+)?$/.test(text) && !/^-?\d+(,\d+)?$/.test(text)) return null;
  return parseLineNumber(text.replace(/\./g, '').replace(',', '.'));
}

/**
 * An extracted value as a German form value. One that already reads as German
 * with the same meaning is kept as written (`22,50` stays `22,50`); any other
 * spelling is converted (`4.20` → `4,2`, `1234` stays `1234`).
 */
export function toGermanInput(raw: unknown): string {
  const canonical = parseLineNumber(raw);
  if (canonical === null) return '';
  if (typeof raw === 'string' && parseGermanInput(raw) === canonical) return raw.trim();
  return canonical.replace('.', ',');
}

function text(row: LineRow, keys: readonly string[]): string {
  for (const key of keys) {
    const value = row[key];
    if (value !== null && value !== undefined && String(value).trim()) return String(value).trim();
  }
  return '';
}

export const rowName = (row: LineRow): string => text(row, ['product_name', 'description']);
export const rowUnit = (row: LineRow): string => text(row, ['quantity_unit', 'unit']);

export function isAnchorableRow(row: LineRow): boolean {
  if (rowName(row)) return true;
  return ['quantity', 'unit_price', 'amount'].some((key) => parseLineNumber(row[key]) !== null);
}

/** A cell as the farmer reads it: strings verbatim, a bare number with a German comma. */
function cell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return String(value).replace('.', ',');
  return String(value).trim();
}

/**
 * "Zeile 2 · Erdbeeren · 120 kg · 4,20". The number is `lineNumber(index)` —
 * counted from 1, as a farmer counts the lines on the paper — exactly as the
 * "Verknüpfte Objekte" panel renders it (`linkFormat.anchorLabel`), so the line
 * a farmer picked is the line the panel names afterwards. The `anchor_ref` sent
 * and stored stays the 0-based index: it is what the link id hashes.
 */
export function rowSummary(row: LineRow, index: number): string {
  const qty = [cell(row.quantity), rowUnit(row)].filter(Boolean).join(' ');
  return [i18n.t('twinLinks.details.line', { n: lineNumber(index) }), rowName(row), qty, cell(row.unit_price)]
    .filter(Boolean)
    .join(' · ');
}

/** The 1-based line number a person reads for a 0-based row index / `anchor_ref`. */
export function lineNumber(index: number | string): string {
  const n = typeof index === 'number' ? index : Number(index);
  return Number.isInteger(n) && n >= 0 ? String(n + 1) : String(index);
}

/** `yyyy-mm-dd` from an ISO date/timestamp or a German `d.m.yyyy`, else `null`. */
export function isoDate(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let y: string, m: string, d: string;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  const de = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(raw.trim());
  if (iso) [, y, m, d] = iso;
  else if (de) [, d, m, y] = de;
  else return null;
  m = m.padStart(2, '0');
  d = d.padStart(2, '0');
  const probe = new Date(`${y}-${m}-${d}T00:00:00Z`);
  if (Number.isNaN(probe.getTime()) || probe.toISOString().slice(0, 10) !== `${y}-${m}-${d}`) return null;
  return `${y}-${m}-${d}`;
}

/** Today as `yyyy-mm-dd`, in the farmer's local calendar. */
export function todayIso(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export interface LinePrefill {
  unitPrice: string;
  quantity: string;
  unit: string;
  currency: string;
  observedAt: string;
}

/**
 * What picking a row fills in — every value stays editable. Rows carry no
 * date, so the observation date is the document's, else today.
 */
export function prefillFromRow(row: LineRow, documentDate: string | null | undefined, today: string): LinePrefill {
  const currency = text(row, ['currency']);
  return {
    // Extracted values come in either convention; the form speaks German, so a
    // prefilled value reads back through `parseGermanInput` unchanged.
    unitPrice: toGermanInput(row.unit_price),
    quantity: toGermanInput(row.quantity),
    unit: rowUnit(row),
    currency: currency ? currency.toUpperCase() : 'EUR',
    observedAt: isoDate(documentDate) ?? today,
  };
}
